/* eslint-disable react-hooks/exhaustive-deps */
import type { LoaderData } from '@ubo/losse-sjedel'
import { LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { motion, useMotionValue } from 'framer-motion'
import useIsMobile from '~/hooks/useIsMobile'
import Animated from '~/components/elements/Animated'
import { type Vacature } from '~/graphql/types'

type Section = {
  title: string
  href: string
}

const SECTIONS: { [key: string]: Section[] } = {
  vacature: [
    {
      title: 'Samenvatting',
      href: '#samenvatting'
    },
    {
      title: 'Organisatie',
      href: '#organisatie'
    },
    {
      title: 'Functie',
      href: '#functie'
    },
    {
      title: 'Aanbod',
      href: '#aanbod'
    },
    {
      title: 'Functie-eisen',
      href: '#functie-eisen'
    },
    {
      title: 'Sollicitatie procedure',
      href: '#sollicitatie-procedure'
    }
  ],
  interneVacature: [
    {
      title: 'Functie',
      href: '#functie'
    },
    {
      title: 'Inhoudelijk',
      href: '#inhoudelijk'
    },
    {
      title: 'Expert',
      href: '#expert'
    },
    {
      title: 'Wat ben je waard',
      href: '#waard'
    },
    {
      title: 'Over Profield',
      href: '#over-profield'
    }
  ]
}

export default function HeaderVacancy() {
  const data = useLoaderData<LoaderData<Vacature>>()
  const contentType = data?.page?.contentType?.node?.name
  const [activeSection, setActiveSection] = useState<number>(null)
  const opacity = useMotionValue(0)
  const [showHeader, setShowHeader] = useState(false)
  const mobile = useIsMobile('(max-width: 640px)')
  const [videoModal, setVideoModal] = useState(false)

  const sections = SECTIONS[contentType] || []

  useEffect(() => {
    if (!sections.length) return

    const handleScroll = () => {
      const sectionElements = sections
        .map((section) => {
          if (!section) return null
          const element = document.querySelector<HTMLDivElement>(section.href)
          return element
        })
        // we reverse the array because we want to find the last section that is active
        .reverse()

      const header = document.querySelector<HTMLDivElement>('[data-component="HeaderVacancy"]')
      if (!header) return
      const headerHeight = header.offsetHeight
      const scrollTop = window.scrollY

      const foundActiveSection = sectionElements.findIndex((section) => {
        if (!section) return false
        const sectionScrollX = section.offsetTop - headerHeight
        // we dont want to trigger the active perfectly, we just want to trigger it when its around the middle of the screen
        const activationOffset = 100

        if (scrollTop >= sectionScrollX - activationOffset) {
          return true
        }

        return false
      })

      // we animate the header a bit before the first section is active
      if (foundActiveSection !== null) {
        const firstSection = document.querySelector<HTMLDivElement>(sections[0].href)
        if (!firstSection) return

        const startAnimationAtScrollY = firstSection.offsetTop - 400
        const endAnimationAtScrollY = firstSection.offsetTop - 200

        // interpolate from 0 to 1, using the scrollTop
        // never be lower than 0
        const opac = Math.max(0, (scrollTop - startAnimationAtScrollY) / (endAnimationAtScrollY - startAnimationAtScrollY))
        opacity.set(opac)
        setShowHeader(opac > 0)
      }

      // map reversed array index back to original
      setActiveSection(foundActiveSection === -1 ? null : sections.length - foundActiveSection - 1)
    }

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!sections.length) return null

  return (
    <>
      {videoModal && (
        <Animated delay={0}>
          <div
            onClick={() => {
              setVideoModal(false)
            }}
            className="fixed w-full top-0 left-0 h-full flex items-center justify-center z-50 cursor-pointer bg-black bg-opacity-80"
          >
            <div className="container">
              <div onClick={(e) => e.stopPropagation()} className="cursor-auto max-w-screen-md aspect-video mx-auto">
                <iframe
                  width="100%"
                  height="100%"
                  src={data.page.recap.video}
                  title="Inside the V3 Nazi Super Gun"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </Animated>
      )}

      <motion.div
        data-component="HeaderVacancy"
        className={clsx(
          'fixed left-0 top-0 w-full bg-white text-site-base z-30 py-5 lg:border-none border-b-2 border-b-site-conversion block--shadow'
        )}
        style={{ opacity, display: showHeader && !mobile ? 'block' : 'none' }}
      >
        <div className="container flex lg:flex-row flex-col w-full gap-10">
          <div className="flex flex-col justify-between lg:w-[80%]">
            <span className="font-bold xl:text-2xl text-xl">{data.page.title}</span>

            <div className="items-center gap-x-8 mt-4 xl:text-lg text-base lg:flex hidden">
              {sections.map((section, index) => {
                const isActive = activeSection === index

                return (
                  <a
                    key={index}
                    href={section.href}
                    onClick={(e) => {
                      if (mobile) return
                      e.preventDefault()
                      const element = document.querySelector<HTMLDivElement>(section.href)
                      if (!element) return
                      const header = document.querySelector<HTMLDivElement>('[data-component="HeaderVacancy"]')
                      if (!header) return
                      const headerHeight = header.offsetHeight
                      const sectionScrollX = element.offsetTop - headerHeight
                      window.scrollTo({ top: sectionScrollX, behavior: 'smooth' })
                    }}
                    className={clsx('hover:text-site-accent transition-colors', isActive ? 'text-site-accent' : 'text-site-grey-other')}
                  >
                    {section.title}

                    <div className="relative h-1 w-full">
                      <motion.div
                        animate={{ width: isActive ? '100%' : '0' }}
                        className="h-1 bg-site-accent rounded-full absolute top-0 left-0"
                      />
                    </div>
                  </a>
                )
              })}
            </div>
          </div>

          <div className="lg:w-[20%] flex lg:flex-col justify-center flex-row gap-4">
            {data.page.recap.video && (
              <div onClick={() => setVideoModal(true)} className="btn--accent min-w-[288px] relative">
                <span className="relative">
                  <svg
                    className="absolute top-0 bottom-0 -left-8"
                    width="24"
                    height="23"
                    viewBox="0 0 24 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_3036_4305)">
                      <path
                        d="M18.2295 23H5.7712C4.37375 22.9985 3.03398 22.4427 2.04584 21.4545C1.0577 20.4664 0.501888 19.1266 0.500366 17.7292L0.500366 5.27083C0.501888 3.87339 1.0577 2.53362 2.04584 1.54547C3.03398 0.557329 4.37375 0.001522 5.7712 0L18.2295 0C19.627 0.001522 20.9667 0.557329 21.9549 1.54547C22.943 2.53362 23.4988 3.87339 23.5004 5.27083V17.7292C23.4988 19.1266 22.943 20.4664 21.9549 21.4545C20.9667 22.4427 19.627 22.9985 18.2295 23ZM5.7712 2.875C5.13579 2.875 4.5264 3.12742 4.07709 3.57672C3.62778 4.02603 3.37537 4.63542 3.37537 5.27083V17.7292C3.37537 18.3646 3.62778 18.974 4.07709 19.4233C4.5264 19.8726 5.13579 20.125 5.7712 20.125H18.2295C18.8649 20.125 19.4743 19.8726 19.9236 19.4233C20.3729 18.974 20.6254 18.3646 20.6254 17.7292V5.27083C20.6254 4.63542 20.3729 4.02603 19.9236 3.57672C19.4743 3.12742 18.8649 2.875 18.2295 2.875H5.7712ZM8.15937 8.94987V14.0501C8.15937 14.2745 8.21831 14.495 8.33028 14.6894C8.44225 14.8839 8.60333 15.0456 8.7974 15.1582C8.99146 15.2709 9.21171 15.3306 9.43611 15.3314C9.6605 15.3322 9.88117 15.274 10.076 15.1627L15.1811 12.6126C15.3773 12.5006 15.5403 12.3386 15.6538 12.1432C15.7672 11.9478 15.8269 11.7259 15.8269 11.5C15.8269 11.2741 15.7672 11.0522 15.6538 10.8568C15.5403 10.6614 15.3773 10.4994 15.1811 10.3874L10.076 7.83725C9.88117 7.72598 9.6605 7.66783 9.43611 7.66862C9.21171 7.66942 8.99146 7.72913 8.7974 7.84179C8.60333 7.95445 8.44225 8.11609 8.33028 8.31056C8.21831 8.50502 8.15937 8.72548 8.15937 8.94987Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3036_4305">
                        <rect width="23" height="23" fill="white" transform="translate(0.500366)" />
                      </clipPath>
                    </defs>
                  </svg>
                  Vacature in 60 sec.
                </span>
                <LossePlaatjie
                  className="absolute my-auto top-0 bottom-0 right-2 w-[35px] h-[35px] rounded-full object-top object-cover"
                  src={data.page.recap.advisor.featuredImage.node}
                />
              </div>
            )}
            <a href="#solliciteren" className="btn--conversion min-w-[288px]">
              Solliciteer direct
            </a>
          </div>
        </div>
      </motion.div>
    </>
  )
}
